/**
 * 📸 STORIES TAB - Architecture Mozart/Jobs/Tesla
 * 
 * L'onglet des stories 24h.
 */

import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Plus, Eye, Heart, Clock, Sparkles } from 'lucide-react';
import { Button } from '../../ui/button';
import { StoriesFeed, StoryCreator, StoryViewer } from '../features/stories';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const StoriesTab = ({ customerId, isDarkMode, profile }) => {
  const [stories, setStories] = useState([]);
  const [myStories, setMyStories] = useState([]);
  const [showCreator, setShowCreator] = useState(false);
  const [viewingStory, setViewingStory] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [moods, setMoods] = useState([]);

  // Charger les moods disponibles
  const loadMoods = async () => {
    try {
      const response = await fetch(`${API_URL}/api/stories/moods`);
      const data = await response.json();
      if (data.success) {
        setMoods(data.moods || []);
      }
    } catch (err) {
      console.error('Error loading moods:', err);
    }
  };

  // Charger le feed
  const loadStories = async () => {
    if (!customerId) return;
    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/stories/feed/${customerId}`);
      const data = await response.json();
      if (data.success) {
        setStories(data.stories || []);
      }
    } catch (err) {
      console.error('Error loading stories:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // Charger mes stories
  const loadMyStories = async () => {
    if (!customerId) return;
    try {
      const response = await fetch(`${API_URL}/api/stories/my/${customerId}`);
      const data = await response.json();
      if (data.success) {
        setMyStories(data.stories || []);
      }
    } catch (err) {
      console.error('Error loading my stories:', err);
    }
  };

  // Créer une story
  const createStory = async (content, mood) => {
    if (!customerId) return;
    try {
      const response = await fetch(`${API_URL}/api/stories/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customerId,
          content,
          mood,
          type: 'text'
        })
      });
      const data = await response.json();
      if (data.success) {
        toast.success('✨ Story publiée !');
        setShowCreator(false);
        loadStories();
        loadMyStories();
      }
    } catch (err) {
      console.error('Error creating story:', err);
      toast.error('Erreur lors de la publication');
    }
  };

  // Réagir à une story
  const reactToStory = async (storyId, reaction) => {
    try {
      await fetch(`${API_URL}/api/stories/${storyId}/react`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ viewerId: customerId, reaction })
      });
    } catch (err) {
      console.error('Error reacting:', err);
    }
  };

  useEffect(() => {
    loadMoods();
    loadStories();
    loadMyStories();
  }, [customerId]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            📸 Stories
          </h2>
          <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            Partage ton mood du moment
          </p>
        </div>
        <Button
          onClick={() => setShowCreator(true)}
          className="bg-gradient-to-r from-purple-500 to-pink-500 text-white"
        >
          <Plus className="w-4 h-4 mr-1" />
          Créer
        </Button>
      </div>

      {/* Mes Stories */}
      {myStories.length > 0 && (
        <div>
          <h3 className={`text-sm font-medium mb-3 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            Mes Stories
          </h3>
          <div className="flex gap-3 overflow-x-auto pb-2">
            {myStories.map(story => (
              <motion.div
                key={story.id}
                whileHover={{ scale: 1.05 }}
                onClick={() => setViewingStory(story)}
                className="flex-shrink-0 w-20 cursor-pointer"
              >
                <div className={`w-20 h-20 rounded-full p-1 bg-gradient-to-br from-purple-500 to-pink-500`}>
                  <div className={`w-full h-full rounded-full flex items-center justify-center text-3xl ${
                    isDarkMode ? 'bg-gray-800' : 'bg-white'
                  }`}>
                    {story.mood?.emoji || '✨'}
                  </div>
                </div>
                <p className={`text-xs text-center mt-1 truncate ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  {story.mood?.label || 'Story'}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      )}

      {/* Feed */}
      {isLoading ? (
        <div className="text-center py-12">
          <div className="animate-spin text-4xl">📸</div>
          <p className={`mt-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Chargement...</p>
        </div>
      ) : stories.length === 0 ? (
        <div className="text-center py-12">
          <div className="text-5xl mb-4">📸</div>
          <h3 className={`text-lg font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            Pas encore de stories
          </h3>
          <p className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>
            Sois le premier à partager ton mood !
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          <h3 className={`text-sm font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            <Sparkles className="w-4 h-4 inline mr-1" />
            Stories du jour
          </h3>
          <div className="flex gap-3 overflow-x-auto pb-2">
            {stories.map(story => (
              <motion.div
                key={story.id}
                whileHover={{ scale: 1.05 }}
                onClick={() => setViewingStory(story)}
                className="flex-shrink-0 w-20 cursor-pointer"
              >
                <div className={`w-20 h-20 rounded-full p-1 ${
                  story.viewedBy?.includes(customerId) 
                    ? 'bg-gray-400' 
                    : 'bg-gradient-to-br from-purple-500 to-pink-500'
                }`}>
                  <div className={`w-full h-full rounded-full flex items-center justify-center text-3xl overflow-hidden ${
                    isDarkMode ? 'bg-gray-800' : 'bg-white'
                  }`}>
                    {story.authorPhoto ? (
                      <img src={story.authorPhoto} alt="" className="w-full h-full object-cover" />
                    ) : (
                      story.authorAvatar || '😊'
                    )}
                  </div>
                </div>
                <p className={`text-xs text-center mt-1 truncate ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  {story.authorName}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      )}

      {/* Story Creator Modal */}
      {showCreator && (
        <StoryCreator
          isDarkMode={isDarkMode}
          moods={moods}
          onClose={() => setShowCreator(false)}
          onSubmit={createStory}
        />
      )}

      {/* Story Viewer Modal */}
      {viewingStory && (
        <StoryViewer
          story={viewingStory}
          isDarkMode={isDarkMode}
          onClose={() => setViewingStory(null)}
          onReact={reactToStory}
          isOwn={viewingStory.customerId === customerId}
        />
      )}
    </div>
  );
};

export default StoriesTab;
